import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtINR, fmtUSD } from "@/lib/money";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUSES = ["pending", "paid", "shipped", "delivered", "cancelled"];

const STATUS_CLASS = {
  pending: "bg-[#FFB300] text-[#2C1810]",
  paid: "bg-[#E65C00] text-white",
  shipped: "bg-[#4A148C] text-[#FFCF52]",
  delivered: "bg-[#0F7B3B] text-white",
  cancelled: "bg-gray-500 text-white",
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);

  async function load() {
    try { const { data } = await api.get("/orders/all"); setOrders(data); } catch {}
  }
  useEffect(() => { load(); }, []);

  async function update(id, status) {
    try {
      await api.put(`/orders/${id}/status`, { status });
      toast.success("Order updated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-display text-4xl text-[#2C1810]">Orders</h1>
        <p className="font-body text-[#5B3D2A]">Track and fulfil customer orders.</p>
      </div>

      <div className="bg-white rounded-2xl border border-[#FFD1A6] shadow-warm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Items</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Payment</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-10 font-body text-[#5B3D2A]">No orders yet.</TableCell></TableRow>
            )}
            {orders.map((o) => (
              <TableRow key={o.id} data-testid={`admin-order-${o.id}`}>
                <TableCell>
                  <div className="font-body text-sm font-semibold text-[#2C1810]">#{o.id.slice(0, 8)}</div>
                  <div className="text-xs font-body text-[#5B3D2A]">{new Date(o.created_at).toLocaleString()}</div>
                </TableCell>
                <TableCell className="font-body text-sm">{o.user_email}</TableCell>
                <TableCell className="font-body text-xs">
                  {o.items.map((i) => `${i.name} × ${i.quantity}`).join(", ")}
                </TableCell>
                <TableCell className="font-display text-[#E65C00] font-semibold">
                  <div>₹{fmtINR(o.total)}</div>
                  <div className="text-[10px] font-body font-normal text-[#5B3D2A]/70">≈ ${fmtUSD(o.total)}</div>
                </TableCell>
                <TableCell>
                  <span className={`text-[10px] px-2 py-1 rounded-full ${o.payment_status === "paid" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
                    {o.payment_status}
                    {o.payment_provider ? ` · ${o.payment_provider}` : ""}
                  </span>
                </TableCell>
                <TableCell>
                  <Select value={o.status} onValueChange={(v) => update(o.id, v)}>
                    <SelectTrigger data-testid={`status-select-${o.id}`} className={`w-[130px] h-8 text-xs rounded-full ${STATUS_CLASS[o.status] || ""}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
